/**
 * src/components/mfa/AssistantSecurity.tsx
 * アシスタントの「ログイン・セキュリティ」。
 *   ・はじめて設定する人（方式が未設定）: ステップ式（STEP1 メール確認 → STEP2 方法の選択 → STEP3 登録と完了）。いつでも「あとで設定する」でやめられる
 *   ・設定済みの人: 現在の状態だけを表示し、変更・管理は「認証方法を変更・管理する」から
 * 通信・再認証・メール確認の処理は SecuritySettings／EmailVerify の既存のものを使う（ここでは新しい認証処理を作らない）。
 * 端末の前提: スタジオの共用パソコン（Windows）と、本人のスマートフォン。パスキーは「自分のスマホ」への登録を基本にする。
 *
 * ※ 保存先の指定（パソコンから「スマートフォン」を選ばせる）はブラウザ／OSのヒントにすぎず、Windows Hello などが
 *    表示される場合がある。画面では「必ず除外できる」とは言わず、選ぶ場所の案内と、登録後の確認（保存先の目安・ログイン確認）で補う。
 */

import { useEffect, useState } from 'react'
import { METHOD_LABEL, type MfaMethod, type MfaSettings } from '../../api/mfa'
import { isPasskeySupported, type PasskeyTarget } from '../../lib/webauthn'
import { useAuth } from '../AuthGuard'
import { EmailVerifyPanel, type EmailVerifyState } from './EmailVerify'
import {
  deviceKind, emailReady, isFirstSetup, passkeyLoginConfirmed, showLocalOnDesktopWarning, startView, storageHint,
  type MethodChoice, type SetupView,
} from './setupFlow'

type Msg = { ok: boolean; text: string } | null
type View = SetupView | 'done' | 'idle'

export type SecurityCtl = {
  s: MfaSettings
  isMobile: boolean
  forceSetup: boolean
  busy: string | null
  msg: Msg
  clearMsg: () => void
  addPasskey: (target: PasskeyTarget | undefined, name: string) => Promise<boolean>
  changeMethod: (m: MfaMethod) => Promise<boolean>
  /** メールが未確認なら、確認のダイアログを先に出し、確認が済んだら run を実行する（キャンセルなら何もしない） */
  guard: (label: string, run: () => void) => void
  ev: EmailVerifyState
  /** 既存の詳細設定（パスキーの一覧・方式の変更・メールアドレス）。折りたたんで表示する */
  manage: React.ReactNode
}

const pad = (isMobile: boolean) => ({ padding: isMobile ? '16px 16px' : '20px 24px' })

function Banner({ msg }: { msg: Msg }) {
  if (!msg) return null
  return (
    <div role={msg.ok ? 'status' : 'alert'} style={{
      background: msg.ok ? 'var(--green-light)' : 'var(--red-light)', color: msg.ok ? 'var(--green)' : 'var(--red)',
      borderRadius: 8, padding: '10px 14px', fontSize: 13, margin: '12px 0', lineHeight: 1.6,
    }}>{msg.ok ? '✓ ' : '⚠ '}{msg.text}</div>
  )
}

/** メールアドレスを確認して、ログインIDが変わったときの案内 */
function LoginIdChanged({ to }: { to: string | null }) {
  if (!to) return null
  return (
    <div role="status" style={{ background: 'var(--amber-light)', color: 'var(--ink)', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
      <strong>ログインIDが変わりました。</strong>次回から、<span style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{to}</span> と今までのパスワードでログインします（メモしてください）。
    </div>
  )
}

function Stepper({ index }: { index: 1 | 2 | 3 }) {
  const items = ['メールの確認', '認証方法を選ぶ', '登録して完了']
  return (
    <ol aria-label="設定の手順" style={{ display: 'flex', gap: 6, listStyle: 'none', padding: 0, margin: '0 0 16px', flexWrap: 'wrap' }}>
      {items.map((t, i) => {
        const n = i + 1
        const state = n < index ? 'done' : n === index ? 'now' : 'next'
        return (
          <li key={t} aria-current={state === 'now' ? 'step' : undefined} style={{
            fontSize: 12, padding: '4px 10px', borderRadius: 999, fontWeight: state === 'now' ? 700 : 500,
            background: state === 'now' ? 'var(--blue-light)' : state === 'done' ? 'var(--green-light)' : 'var(--paper-2)',
            color: state === 'done' ? 'var(--green)' : state === 'now' ? 'var(--ink)' : 'var(--ink-3)',
          }}>
            {state === 'done' ? '✓' : `STEP ${n}`} {t}
          </li>
        )
      })}
    </ol>
  )
}

function Choice({ title, badge, desc, action, onClick, disabled, note, recommended }: {
  title: string; badge?: string; desc: string; action: string; onClick: () => void
  disabled?: boolean; note?: string | null; recommended?: boolean
}) {
  return (
    <div style={{
      border: `1.5px solid ${recommended ? 'var(--green)' : 'var(--paper-3)'}`, borderRadius: 12, padding: '14px 16px',
      background: recommended ? 'var(--green-light)' : 'var(--paper)',
    }}>
      <div style={{ fontWeight: 700, fontSize: 15 }}>{title}{badge && <span className="badge badge-green" style={{ marginLeft: 8 }}>{badge}</span>}</div>
      <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, margin: '6px 0 12px' }}>{desc}</div>
      <button className="btn btn-primary" disabled={disabled} onClick={onClick} style={{ width: '100%', minHeight: 46, fontSize: 15 }}>{action}</button>
      {note && <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 8, lineHeight: 1.6 }}>※ {note}</div>}
    </div>
  )
}

/** パソコン内蔵の保存先（Windows Hello など）に保存しようとしたときの注意 */
function SharedPcWarning({ onContinue, onBack }: { onContinue: () => void; onBack: () => void }) {
  return (
    <div className="overlay" style={{ zIndex: 1250 }}>
      <div className="modal" style={{ maxWidth: 440 }} role="dialog" aria-modal="true" aria-label="このパソコンに保存する前に">
        <div className="modal-header"><div className="modal-title">⚠ このパソコンに保存しますか？</div></div>
        <div className="modal-body">
          <p style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.8, marginTop: 0 }}>
            スタジオの<strong>共用パソコン</strong>に保存すると、<strong>そのパソコンでしかログインできなくなる</strong>ことがあります（帰宅後にスマホでログインできません）。
            また、ほかの人が使える Windows アカウントに、あなたのパスキーが残る場合があります。
          </p>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8 }}>
            <strong>自分専用のパソコン</strong>のときだけ、保存してください。共用パソコンの場合は、自分のスマホに登録します（パソコンでも、スマホの認証で使えます）。
          </p>
          <button className="btn btn-primary" style={{ width: '100%', minHeight: 46 }} onClick={onBack}>自分のスマホに登録する（おすすめ）</button>
          <button className="btn btn-outline" style={{ width: '100%', minHeight: 44, marginTop: 8 }} onClick={onContinue}>自分専用のパソコンなので、このパソコンに保存する</button>
        </div>
      </div>
    </div>
  )
}

function PasskeyTips() {
  return (
    <details style={{ marginTop: 14, fontSize: 13, color: 'var(--ink-2)' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>うまくいかないとき</summary>
      <ul style={{ lineHeight: 1.9, paddingLeft: 18, margin: '8px 0 0' }}>
        <li><strong>保存先を選ぶ画面に「Windows Hello」などパソコンの名前が出たとき</strong>は、共用パソコンには保存せず、「iPhone、iPad、Android デバイス」「別のデバイスを使用」「別の方法」など、<strong>スマートフォンを選ぶ</strong>ものを選びます（ブラウザやパソコンによって、表示は変わります）。</li>
        <li><strong>QRコードが出ない・読み取れないとき</strong>: パソコンとスマホの<strong>Bluetoothをオン</strong>にし、スマホの標準のカメラで読み取ります。近くに置いてください。</li>
        <li>それでもできないときは、<strong>スマホのブラウザでMangaOSを開いてログイン</strong>し、スマホから直接登録します。</li>
        <li>「キャンセルされました」と出たら、もう一度ボタンを押してやり直せます。</li>
        <li>パスキーが難しい場合は、戻って<strong>メール認証</strong>を選べます。困ったときは管理者に連絡してください。</li>
      </ul>
    </details>
  )
}

function SharedPcNotes() {
  return (
    <div style={{ background: 'var(--paper-2)', borderRadius: 10, padding: '12px 14px', fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.9 }}>
      <strong>スタジオの共用パソコンを使うとき</strong>
      <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
        <li>ブラウザに<strong>パスワードを保存しない</strong>（「保存しますか？」と出たら「保存しない」）</li>
        <li>使い終わったら、必ず<strong>ログアウト</strong>する</li>
        <li>スマホに登録したパスキーは、パソコンにはコピーされません。パソコンでは、QRコード＋スマホの認証で使います</li>
        <li>認証の画面に、<strong>ほかの人のパスキーが候補として表示される</strong>ことがあります（表示はパソコンやブラウザの仕組みで、MangaOS では制御できません）。<strong>自分のスマホ以外は選ばない</strong>でください。ほかの人のパスキーでは、あなたのアカウントにはログインできません（サーバーが拒否します）</li>
      </ul>
    </div>
  )
}

export default function AssistantSecurity({ ctl }: { ctl: SecurityCtl }) {
  const { s, isMobile, ev, busy } = ctl
  const { user } = useAuth()
  const input = {
    isAssistant: true,
    method: s.method,
    emailVerified: s.email.verified,
    emailPlaceholder: s.email.placeholder,
    mustChangePassword: !!user?.must_change_password,
  }
  const ready = emailReady(input)
  const first = isFirstSetup(input)
  const kind = deviceKind(typeof navigator === 'undefined' ? '' : navigator.userAgent)
  const supported = isPasskeySupported()
  // 必須化で設定が義務になっているとき（設定が終わるまで他の機能は使えない）は、やめられない
  const mustFinish = ctl.forceSetup || s.setup_required

  const [view, setView] = useState<View>(() => startView(input))
  const [doneKind, setDoneKind] = useState<MethodChoice | null>(null)
  const [sharedWarn, setSharedWarn] = useState(false)
  const [more, setMore] = useState(false)

  // メール確認が完了したら STEP 1 → STEP 2 へ進む（STEP 1 を飛ばす選択肢はない。確認は初回設定の中で一度だけ）
  useEffect(() => {
    if (first && ready && view === 'email') setView('choose')
  }, [first, ready, view])

  // 初期パスワードの変更が済んだら（画面を開き直したとき）、最初の画面を決め直す
  useEffect(() => {
    if (view === 'password' && !input.mustChangePassword) setView(startView(input))
  }, [view, input.mustChangePassword]) // eslint-disable-line react-hooks/exhaustive-deps

  /** 設定全体をやめる（あとで設定する）。途中のメール確認の入力も破棄し、何も変更しない */
  const leave = () => { ctl.clearMsg(); ev.reset(); setView('idle') }
  const start = () => { ctl.clearMsg(); ev.reset(); setView(startView(input)) }

  /** 方式を選ぶ。メールが未確認（STEP 1 を経ていない場合など）は、確認のダイアログが先に出て、確認後に自動で続く */
  const choose = (c: MethodChoice) => {
    ctl.clearMsg()
    ctl.guard(c === 'email' ? 'メール認証を設定する' : 'パスキーを登録する', () => setView(c === 'passkey' ? 'passkey' : 'emailMethod'))
  }

  const registerPasskey = async (target: PasskeyTarget | undefined) => {
    const name = target === 'device' && kind === 'desktop' ? 'このパソコン' : 'スマートフォン'
    const ok = await ctl.addPasskey(target, name)
    if (ok) { setDoneKind('passkey'); setView('done') }
  }
  const useExistingPasskey = async () => {
    if (await ctl.changeMethod('passkey')) { setDoneKind('passkey'); setView('done') }
  }
  const setEmailMethod = async () => {
    if (await ctl.changeMethod('email')) { setDoneKind('email'); setView('done') }
  }

  const methodBadge = s.method === 'none'
    ? <span className="badge badge-amber">未設定</span>
    : <span className="badge badge-green">{METHOD_LABEL[s.method]}</span>
  const hasUsablePasskey = s.passkeys.some((p) => p.usable)
  const passkeyOk = s.passkey_available && supported
  const stepIndex: 1 | 2 | 3 = view === 'email' || view === 'password' ? 1 : view === 'choose' ? 2 : 3

  // ── 設定済み／設定直後の画面 ───────────────────────────────
  if (!first || view === 'done') {
    const justDone = view === 'done' && doneKind
    const method = s.method
    const loggedIn = passkeyLoginConfirmed(s.passkeys)
    // 直近に登録したパスキー（作成日時が最新）。保存先の目安を表示する
    const latest = [...s.passkeys].sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))).pop()
    const hint = latest ? storageHint(latest) : null
    const localOnDesktop = !!hint && showLocalOnDesktopWarning(kind, hint)
    return (
      <>
        {justDone && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header"><span className="card-title">✓ 設定できました</span></div>
            <div style={pad(isMobile)}>
              <LoginIdChanged to={ev.changedTo} />
              <div style={{ fontSize: 14, lineHeight: 1.8, marginBottom: 12 }}>
                {doneKind === 'passkey'
                  ? <>パスキーを登録しました。<strong>登録して終わりではなく、使う場所でログインできるか、確認してください。</strong></>
                  : <>メール認証を設定しました。ログインのたびに、メールに6桁のコードが届きます（スマホでもパソコンでも同じです）。</>}
              </div>
              {doneKind === 'passkey' && latest && hint && (
                <div style={{ fontSize: 13, background: 'var(--paper-2)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.8 }}>
                  登録されたパスキー：<strong>{latest.name}</strong>
                  {hint === 'synced'
                    ? <span className="badge badge-green" style={{ marginLeft: 8 }}>同期あり（スマホなどに保存）</span>
                    : <span className="badge badge-amber" style={{ marginLeft: 8 }}>この端末の中だけ</span>}
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>※ 保存先の「目安」です（同期されるかどうかから推測しています）。実際に、下の確認でログインできるか試してください。</div>
                </div>
              )}
              {doneKind === 'passkey' && localOnDesktop && (
                <div style={{ fontSize: 13, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.7 }}>
                  <strong>このパソコンの中だけに保存された可能性があります。</strong>共用パソコンの場合、そのパソコンでしかログインできず、ほかの人が使える Windows アカウントに残ることがあります。
                  自分のスマホでもログインしたい場合は、スマホ用のパスキーも登録してください。
                  <button className="btn btn-outline btn-sm" style={{ display: 'block', marginTop: 8 }} disabled={busy !== null}
                    onClick={() => void registerPasskey('phone')}>スマホ用のパスキーも登録する</button>
                </div>
              )}
              <ol style={{ fontSize: 13.5, lineHeight: 1.9, paddingLeft: 20, margin: '0 0 12px' }}>
                {doneKind === 'passkey' ? (
                  <>
                    <li><strong>自分のスマホで</strong>：いちどログアウトして、もう一度ログイン →「パスワード」のあと、顔認証・指紋で入れること
                      {loggedIn
                        ? <span className="badge badge-green" style={{ marginLeft: 8 }}>ログインで使った記録あり</span>
                        : <span className="badge badge-amber" style={{ marginLeft: 8 }}>まだ使っていません</span>}
                    </li>
                    <li><strong>スタジオのパソコンで</strong>：パスワードのあと、「スマートフォンを使用」（または「別のデバイスを使用」）を選ぶ → QRコードをスマホのカメラで読み取り → スマホで顔認証・指紋（<strong>PC・スマホのBluetoothをオン</strong>）</li>
                    <li>この<strong>2つで入れて、はじめて完了</strong>です。どちらかで入れないときは、管理者に連絡してください（設定をやり直せます）</li>
                  </>
                ) : (
                  <>
                    <li>いちどログアウトして、もう一度ログイン →「パスワード」のあと、メールに届く6桁のコードを入力して入れること</li>
                    <li>スタジオのパソコンでも、スマホでも、同じ方法で入れます</li>
                  </>
                )}
              </ol>
              <SharedPcNotes />
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.8, marginTop: 10 }}>
                スマホを替えた・なくした・メールが届かないときは、管理者に連絡してください（設定をやり直せます）。
              </div>
            </div>
          </div>
        )}
        {!justDone && <Banner msg={ctl.msg} />}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><span className="card-title">ログインの追加認証</span></div>
          <div style={pad(isMobile)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>現在の方式</span>
              {methodBadge}
              {s.required && <span className="badge badge-blue">必須</span>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginTop: 10 }}>
              {method === 'passkey' && 'パスワードのあと、パスキー（顔認証・指紋など）で本人確認をします。'}
              {method === 'email' && 'パスワードのあと、メールに届く6桁のコードで本人確認をします。'}
              {method === 'none' && 'まだ設定していません。'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12, fontSize: 13 }}>
              <span style={{ color: 'var(--ink-3)' }}>メールアドレス</span>
              <span style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{s.email.address}</span>
              {s.email.placeholder
                ? <span className="badge badge-amber">仮のアドレス</span>
                : s.email.verified ? <span className="badge badge-green">確認済み</span> : <span className="badge badge-amber">未確認</span>}
            </div>
            {!ready && method !== 'none' && (
              <div style={{ fontSize: 12.5, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginTop: 12, lineHeight: 1.7 }}>
                メールアドレスが確認できていません。通知メールが届くよう、下の「認証方法を変更・管理する」から確認してください。
              </div>
            )}
            <button className="btn btn-outline" style={{ marginTop: 14, width: isMobile ? '100%' : undefined }} aria-expanded={more}
              onClick={() => setMore((v) => !v)}>
              {more ? '閉じる ▲' : '認証方法を変更・管理する ▼'}
            </button>
          </div>
        </div>
        {more && ctl.manage}
      </>
    )
  }

  // ── はじめての設定（ステップ式）──────────────────────────────
  const leaveButton = !mustFinish && (
    <button className="btn btn-ghost btn-sm" style={{ marginTop: 14 }} disabled={busy !== null} onClick={leave}>
      あとで設定する（やめる）
    </button>
  )

  return (
    <>
      {sharedWarn && (
        <SharedPcWarning
          onBack={() => { setSharedWarn(false); void registerPasskey('phone') }}
          onContinue={() => { setSharedWarn(false); void registerPasskey('device') }} />
      )}

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><span className="card-title">ログインの追加認証を設定する</span></div>
        <div style={pad(isMobile)}>
          {view === 'idle' ? (
            <>
              <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
                追加認証は、まだ設定していません（今は任意です。これまでどおり使えます）。設定すると、パスワードのあとにもう一つ本人確認をして、アカウントをより安全に守れます。
              </div>
              <button className="btn btn-primary" onClick={start} style={{ width: isMobile ? '100%' : undefined, minHeight: 46 }}>設定を始める</button>
            </>
          ) : (
            <>
              <Stepper index={stepIndex} />
              <LoginIdChanged to={ev.changedTo} />
            </>
          )}

          {view === 'password' && (
            <>
              <div style={{ fontSize: 14, lineHeight: 1.8 }}>先に、<strong>初期パスワードの変更</strong>を済ませてください。変更が終わってから、この設定に進めます。</div>
              <a className="btn btn-primary" href="/account?force_change_password=1" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 12, minHeight: 46, lineHeight: '46px' }}>
                パスワードを変更する ›
              </a>
            </>
          )}

          {view === 'email' && (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>STEP 1　メールアドレスの確認</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
                あなたのメールアドレスを確認します。メールアドレスは<strong>ログインID</strong>でもあり、設定の通知メールの宛先にもなります。
                入力したアドレスに6桁のコードが届くので、それを入力すれば完了です。<strong>この確認は、最初の一度だけ</strong>です。
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
                いまのアドレス：<span style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>{s.email.address}</span>
                {s.email.placeholder ? '（仮）' : '（未確認）'}
              </div>
              <EmailVerifyPanel ev={ev} currentEmail={s.email.address} placeholder={s.email.placeholder} isMobile={isMobile} mfaAvailable={s.email.mfa_available} />
              {leaveButton}
            </>
          )}

          {view === 'choose' && (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>STEP 2　認証方法を選ぶ</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
                パスワードのあとに、もう一つ本人確認をします。ログインのたびに、選んだ方法で確認します（今は任意です）。
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Choice recommended title="🔐 パスキー" badge="おすすめ"
                  desc="自分のスマホの顔認証・指紋でログインします。スタジオのパソコンでも、スマホの認証で入れます。メールは届きません。"
                  action="パスキーで設定する" onClick={() => choose('passkey')} disabled={busy !== null || !passkeyOk}
                  note={!s.passkey_available ? 'パスキーは現在準備中です。' : !supported ? 'このブラウザではパスキーを使えません（Chrome・Edge・Safari で開いてください）。' : null} />
                <Choice title="✉️ メール認証"
                  desc="ログインのたびに、メールに届く6桁のコードを入力します。パスキーが難しいときに選べます。スマホでもパソコンでも同じです。"
                  action="メール認証で設定する" onClick={() => choose('email')} disabled={busy !== null || !s.email.mfa_available}
                  note={!s.email.mfa_available ? 'メール認証は現在ご利用いただけません。' : null} />
              </div>
              {leaveButton}
            </>
          )}

          {view === 'passkey' && (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>STEP 3　パスキーを登録する</div>
              {hasUsablePasskey && (
                <div style={{ marginBottom: 12 }}>
                  <button className="btn btn-outline" disabled={busy !== null} onClick={() => void useExistingPasskey()}>登録済みのパスキーを使う</button>
                </div>
              )}
              {kind === 'phone' ? (
                <>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
                    いま使っている<strong>このスマホ</strong>に、パスキーを登録します。顔認証・指紋・画面ロックで登録できます。
                    登録すると、スタジオのパソコンでも、このスマホを使ってログインできます。
                  </div>
                  <button className="btn btn-primary" disabled={busy !== null} onClick={() => void registerPasskey('device')}
                    style={{ width: '100%', minHeight: 48, fontSize: 15 }}>
                    {busy === 'add' ? '登録中...' : '自分のスマホにパスキーを登録する'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
                    パスキーは、<strong>自分のスマホ</strong>に登録するのがおすすめです。スマホに入れておけば、スタジオのパソコンでも、帰宅後のスマホでも使えます。
                    <ol style={{ margin: '8px 0 0', paddingLeft: 20, lineHeight: 1.9 }}>
                      <li>下のボタンを押す</li>
                      <li>保存先を選ぶ画面で、<strong>「iPhone、iPad、Android デバイス」（スマートフォン）</strong>を選ぶと、パソコンに<strong>QRコード</strong>が出ます</li>
                      <li>スマホの<strong>カメラ</strong>で読み取り（PC・スマホのBluetoothをオン）、<strong>顔認証・指紋</strong>で登録します</li>
                    </ol>
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--amber)', background: 'var(--amber-light)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, lineHeight: 1.7 }}>
                    ブラウザやパソコンによっては、「<strong>Windows Hello</strong>」などパソコンの名前が先に表示されることがあります。
                    共用パソコンには保存しないでください。<strong>選ぶのは、スマートフォンの方</strong>です。
                  </div>
                  <button className="btn btn-primary" disabled={busy !== null} onClick={() => void registerPasskey('phone')}
                    style={{ width: '100%', minHeight: 48, fontSize: 15 }}>
                    {busy === 'add' ? '登録中...（スマホで操作してください）' : '自分のスマホで登録する（おすすめ）'}
                  </button>
                  <button className="btn btn-ghost btn-sm" disabled={busy !== null} onClick={() => setSharedWarn(true)}
                    style={{ marginTop: 10 }}>
                    このパソコンに保存する（自分専用のパソコンの方だけ）
                  </button>
                </>
              )}
              <Banner msg={ctl.msg} />
              <PasskeyTips />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button className="btn btn-ghost btn-sm" disabled={busy !== null} onClick={() => { ctl.clearMsg(); setView('choose') }}>‹ 認証方法の選択に戻る</button>
              </div>
              {leaveButton}
            </>
          )}

          {view === 'emailMethod' && (
            <>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>STEP 3　メール認証を設定する</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.8, marginBottom: 12 }}>
                ログインのたびに、<strong>{s.email.address}</strong> に6桁のコードが届きます。コードを入力すると入れます。
                メールが届かない状態だとログインできなくなるので、届いたことを確認したうえで設定してください。
              </div>
              <button className="btn btn-primary" disabled={busy !== null} onClick={() => void setEmailMethod()} style={{ width: '100%', minHeight: 48, fontSize: 15 }}>
                {busy === 'm:email' ? '設定中...' : 'メール認証を設定する'}
              </button>
              <Banner msg={ctl.msg} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button className="btn btn-ghost btn-sm" disabled={busy !== null} onClick={() => { ctl.clearMsg(); setView('choose') }}>‹ 認証方法の選択に戻る</button>
              </div>
              {leaveButton}
            </>
          )}
        </div>
      </div>

      {view !== 'password' && (
        <div style={{ marginBottom: 20 }}>
          <button className="btn btn-ghost btn-sm" aria-expanded={more} onClick={() => setMore((v) => !v)}>
            {more ? '詳しい設定を閉じる ▲' : '詳しい設定（パスキーの管理など）▼'}
          </button>
          {more && <div style={{ marginTop: 10 }}>{ctl.manage}</div>}
        </div>
      )}
    </>
  )
}
